import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CalendarCheck, Loader2, MapPin, Phone, Shirt } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bureau() {
  const [params] = useSearchParams();
  const id = params.get("id");
  const [reg, setReg] = useState(null);
  const [error, setError] = useState(false);
  const [date, setDate] = useState("");
  const [heure, setHeure] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [adresse, setAdresse] = useState("Kinshasa, RDC");
  const [planning, setPlanning] = useState({ heures: [], pris: [], fermes: [] });
  const { t } = useLang();

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setAdresse(r.data.bureau_adresse)).catch(() => {});
    axios.get(`${API}/rdv/planning`).then((r) => setPlanning(r.data)).catch(() => {});
  }, []);

  const slotState = (h) => {
    if (!date) return "libre";
    if (planning.fermes.includes(`${date}|${h}`) || planning.fermes.includes(`${date}|*`)) return "ferme";
    return "libre"; // plusieurs rendez-vous autorisés sur un même créneau
  };

  useEffect(() => {
    if (!id) { setError(true); return; }
    axios.get(`${API}/registrations/${id}`).then((r) => {
      setReg(r.data);
      if (r.data.rdv_date) setConfirmed(true);
    }).catch(() => setError(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/registrations/${id}/rdv`, { rdv_date: date, rdv_heure: heure });
      toast.success(t("office.toast.ok"), { description: t("office.toast.ok.x") });
      setConfirmed(true);
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data.detail || t("office.toast.conflict"));
        // Recharger le planning : le créneau vient d'être pris ou fermé
        axios.get(`${API}/rdv/planning`).then((r) => setPlanning(r.data)).catch(() => {});
        setHeure("");
      } else {
        toast.error(t("office.toast.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] text-white flex flex-col items-center justify-center gap-6">
        <p className="text-[#71717A]">{t("mm.notfound")}</p>
        <Link to="/" className="text-[#C5A059] text-xs uppercase tracking-[0.25em] border border-[#C5A059] px-6 py-3">{t("mm.back")}</Link>
      </div>
    );
  }
  if (!reg) {
    return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center"><p className="text-[#71717A] text-xs uppercase tracking-[0.3em]">Chargement…</p></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex items-center justify-center px-6 py-16">
      <div className="max-w-xl w-full border border-[#27272A] bg-[#121214] p-10 lg:p-12" data-testid="bureau-page">
        <img src="/assets/logo-dark-sm.png" alt="D2R" className="h-12 w-auto mb-10" />
        <p className="text-[#C5A059] text-xs uppercase tracking-[0.4em] mb-4">{t("office.eyebrow")}</p>
        <h1 className="font-display text-3xl uppercase font-bold leading-tight">
          {t("office.title")} <span className="text-[#C5A059]">{reg.prenom}</span>
        </h1>
        <p className="text-sm text-[#A1A1AA] mt-4 leading-relaxed">
          {t("office.ref.prefix")} <strong className="text-[#C5A059]">{reg.reference}</strong> {t("office.ref.suffix")}{" "}
          {t("office.text")}
        </p>

        <div className="mt-8 border-2 border-[#C5A059] bg-[#C5A059]/10 p-5 flex items-start gap-4" data-testid="rdv-important-banner">
          <CalendarCheck size={26} className="text-[#C5A059] shrink-0 mt-1" />
          <div>
            <p className="font-display uppercase font-bold text-lg text-[#C5A059]">{t("office.banner.t")}</p>
            <p className="text-sm text-[#E4E4E7] mt-1 leading-relaxed">{t("office.banner.x")}</p>
          </div>
        </div>

        <div className="mt-6 space-y-4 border border-[#27272A] bg-[#0A0A0C] p-6">
          <div className="flex items-start gap-3 text-sm text-[#E4E4E7]" data-testid="rdv-adresse">
            <MapPin size={16} className="text-[#C5A059] shrink-0 mt-0.5" /> <span className="font-medium leading-relaxed">{adresse}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#A1A1AA]">
            <Phone size={16} className="text-[#C5A059] shrink-0" /> +243 846 846 760 · +1 (226) 759-7679
          </div>
          {reg.taille_maillot && (
            <div className="flex items-center gap-3 text-sm text-[#A1A1AA]" data-testid="bureau-jersey-size">
              <Shirt size={16} className="text-[#C5A059] shrink-0" />
              {t("office.jersey")} <strong className="text-[#C5A059]">{reg.taille_maillot}</strong>
            </div>
          )}
        </div>

        {confirmed ? (
          <div className="mt-8 border border-[#C5A059]/40 p-6" data-testid="bureau-confirmed">
            <CalendarCheck size={24} className="text-[#C5A059]" />
            <p className="font-display text-xl uppercase font-medium mt-4">{t("office.done.t")}</p>
            <p className="text-sm text-[#A1A1AA] mt-2">
              {reg.rdv_date || date} · {reg.rdv_heure || heure}. {t("office.done.x")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8" data-testid="rdv-form">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-4">{t("office.choose")}</p>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("office.date")}</label>
              <input type="date" required value={date} onChange={(e) => { setDate(e.target.value); setHeure(""); }} data-testid="rdv-date" className="input-editorial [color-scheme:dark]" />
            </div>
            {date && (
              <div className="mt-6">
                <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("office.time")}</label>
                <div className="grid grid-cols-3 gap-3 mt-2" data-testid="rdv-slots">
                  {(planning.heures.length ? planning.heures : ["09h00", "10h00", "11h00", "14h00", "15h00", "16h00"]).map((h) => {
                    const state = slotState(h);
                    return (
                      <button
                        key={h}
                        type="button"
                        disabled={state !== "libre"}
                        onClick={() => setHeure(h)}
                        data-testid={`rdv-slot-${h}`}
                        className={`py-3 text-sm font-medium border transition-colors duration-300 ${
                          state === "pris"
                            ? "border-[#27272A] text-[#3f3f46] line-through cursor-not-allowed"
                            : state === "ferme"
                              ? "border-red-900/40 text-red-900/60 cursor-not-allowed"
                              : heure === h
                                ? "border-[#C5A059] bg-[#C5A059]/10 text-[#C5A059]"
                                : "border-[#27272A] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059]"
                        }`}
                      >
                        {h}
                        {state === "pris" && <span className="block text-[8px] uppercase tracking-widest no-underline">{t("office.taken")}</span>}
                        {state === "ferme" && <span className="block text-[8px] uppercase tracking-widest">{t("office.closed")}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button type="submit" disabled={loading || !heure} data-testid="rdv-submit" className="mt-8 w-full flex items-center justify-center gap-3 bg-[#C5A059] text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300 disabled:opacity-60">
              {loading && <Loader2 size={15} className="animate-spin" />}
              {t("office.submit")}
            </button>
          </form>
        )}
        <Link to="/" className="block text-center text-[#71717A] text-xs mt-8 hover:text-[#C5A059] transition-colors">← {t("pay.back")}</Link>
      </div>
    </div>
  );
}
